import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faOctagonCheck,
  faBinaryLock,
  faBlockBrickFire,
  faShieldHeart,
} from '@fortawesome/pro-regular-svg-icons';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import PageHeader from '../pagecomponents/PageHeader';

// Styles object
const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  main: {
    flex: 1,
    padding: '40px 20px',
    backgroundColor: '#f9f9f9',
  },
  introContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    textAlign: 'center',
  },
  coreHeader: {
    textAlign: 'center',
    margin: '60px 0 40px',
  },
  coreTitle: {
    fontSize: '60px',
    margin: 0,
    fontWeight: '700',
    lineHeight: '1.1',
  },
  coreRed: {
    color: '#ff2300',
  },
  introText: {
    fontSize: '18px',
    color: '#333',
    lineHeight: '1.7',
    marginBottom: '20px',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
  },
  contentContainer: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '70px',
    flexWrap: 'wrap',
  },
  tabButton: {
    padding: '10px 20px',
    border: '2px solid #333',
    borderRadius: '20px',
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
  },
  activeTabButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '20px',
    backgroundColor: '#333',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
  },
  // Centra horizontalmente el icono y el texto
  contentRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    flexWrap: 'wrap',
  },
  iconLarge: {
    fontSize: '56px',
    color: '#ff2300',
    flex: '0 0 auto',
    marginTop: '4px',
  },
  textContainer: {
    flex: 1,
    textAlign: 'left',
    color: '#333',
    maxWidth: '800px',
  },
  sectionTitle: {
    fontSize: '24px',
    marginBottom: '16px',
    fontWeight: 600,
  },
  bulletList: {
    listStyle: 'none',
    paddingLeft: 0,
    fontSize: '18px',
    fontWeight: 400,
  },
  bulletItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
};

// Asocia cada tab con su icono
const tabIcons = {
  section1: faBinaryLock,
  section2: faBlockBrickFire,
  section3: faShieldHeart,
};

export default function PrivacyPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );
  const [activeTab, setActiveTab] = useState('section1');

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    es: {
      pageHeader: {
        title: 'Privacidad en el diseño: más allá del compliance',
        subtitle:
          'Nuestra plataforma nació segura y privada: mimamos tus datos en este mundo hiperconectado. El compliance llega solo, porque cuando haces las cosas bien, los sellos vienen detrás.',
        ctaText: null,
      },
      claim: {
        pre: 'Privacidad en el',
        highlight: 'corazón',
        text: 'Con nuestra plataforma concebida con privacidad en el centro, tus datos son tuyos y sólo tuyos: cada línea de código parte de ese principio. Procesamos lo mínimo necesario, ciframos por defecto. Queremos que innoves sin preocuparte por fugas; nosotros cuidamos el perímetro mientras tú impulsas tu empresa. La seguridad vive en el diseño, no como un accesorio, y el cumplimiento surge por pura consecuencia.',
      },
      sections: [
        {
          key: 'section1',
          title: 'Privacidad como fundamento',
          bullets: [
            'Protegemos tus datos desde el primer momento: enfoque "Privacy by Design".',
            'Control granular: define quién accede a cada conjunto de información.',
            'Cumplimiento normativo: GDPR, LOPD y estándares internacionales.',
          ],
        },
        {
          key: 'section2',
          title: 'Aislamiento de la Información',
          bullets: [
            'Muros de datos: separamos por completo la información de empleados.',
            'Capas encriptadas: datos en reposo y en tránsito protegidos con cifrado.',
            'Acceso segmentado: permisos basados en roles y necesidades reales.',
          ],
        },
        {
          key: 'section3',
          title: 'Ética y Transparencia',
          bullets: [
            'La plataforma nunca asume ni ejecuta acciones por sí misma.',
            'Ofrece múltiples opciones y escenarios para que el manager decida con pensamiento crítico.',
            'Logs transparentes: cada recomendación de la IA es trazable y auditada.',
          ],
        },
      ],
    },
    en: {
      pageHeader: {
        title: 'Privacy in Design: Beyond compliance',
        subtitle:
          'Our platform was born secure and private: we pamper your data in this hyper-connected world. Compliance follows naturally, because when you do things right, certifications come afterward.',
        ctaText: null,
      },
      claim: {
        pre: 'Privacy at the',
        highlight: 'heart',
        text: 'With our platform conceived with privacy at its core, your data is yours and yours alone: every line of code starts from that principle. We process only what is strictly necessary and encrypt by default. We want you to innovate without worrying about leaks; we guard the perimeter while you drive your business. Security lives in the design, never as an add-on, and compliance emerges as a natural consequence.',
      },
      sections: [
        {
          key: 'section1',
          title: 'Privacy as a Foundation',
          bullets: [
            'We protect your data from the very first moment: a “Privacy by Design” approach.',
            'Granular control: define who can access each set of information.',
            'Regulatory compliance: GDPR, LOPD and international standards.',
          ],
        },
        {
          key: 'section2',
          title: 'Information Isolation',
          bullets: [
            'Data walls: we fully separate employee information.',
            'Encrypted layers: data at rest and in transit protected with encryption.',
            'Segmented access: role-based permissions tied to real needs.',
          ],
        },
        {
          key: 'section3',
          title: 'Ethics and Transparency',
          bullets: [
            'The platform never assumes or executes actions on its own.',
            'It offers multiple options and scenarios so managers decide with critical thinking.',
            'Transparent logs: every AI recommendation is traceable and auditable.',
          ],
        },
      ],
    },
  };

  const lang = content[language];
  const current =
    lang.sections.find((s) => s.key === activeTab) || lang.sections[0];

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      <PageHeader
        title={lang.pageHeader.title}
        subtitle={lang.pageHeader.subtitle}
        ctaText={lang.pageHeader.ctaText}
        onCtaClick={() => {}}
      />

      <main style={styles.main}>
        {/* CLAIM */}
        <div style={styles.introContainer}>
          <hr
            style={{
              border: 'none',
              height: '2px',
              backgroundColor: '#ff2300',
              margin: '60px 0',
            }}
          />
          <div style={styles.coreHeader}>
            <h2 style={styles.coreTitle}>
              <span style={{ color: '#222', marginRight: '8px' }}>
                {lang.claim.pre}
              </span>
              <span style={styles.coreRed}>{lang.claim.highlight}</span>
            </h2>
          </div>
          <p style={styles.introText}>{lang.claim.text}</p>
          <hr
            style={{
              border: 'none',
              height: '2px',
              backgroundColor: '#ff2300',
              margin: '60px 0',
              marginBottom: '150px',
            }}
          />
        </div>

        {/* Tabs */}
        <div style={styles.contentContainer}>
          <div style={styles.tabContainer}>
            {lang.sections.map((section) => (
              <button
                key={section.key}
                style={
                  section.key === activeTab
                    ? styles.activeTabButton
                    : styles.tabButton
                }
                onClick={() => setActiveTab(section.key)}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Icon + Text content */}
          <div style={styles.contentRow}>
            <FontAwesomeIcon
              icon={tabIcons[activeTab]}
              style={styles.iconLarge}
            />
            <div style={styles.textContainer}>
              <h3 style={styles.sectionTitle}>{current.title}</h3>
              <ul style={styles.bulletList}>
                {current.bullets.map((item, i) => (
                  <li key={i} style={styles.bulletItem}>
                    <FontAwesomeIcon icon={faOctagonCheck} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}
